import React, { useMemo, useRef, useState } from "react";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseIsoDate = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDate = (value) => {
  const date = parseIsoDate(value);
  if (!date) return "";

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

function CustomDateInput({
  name,
  value,
  onChange,
  onBlur,
  className = "",
  style,
  placeholder = "Select date",
}) {
  const rootRef = useRef(null);
  const selectedDate = parseIsoDate(value);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  const visibleDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const start = new Date(year, month, 1 - firstDay.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }, [viewDate]);

  const handleChangeMonth = (direction) => {
    setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
  };

  const handleSelectDate = (date) => {
    const isoValue = toIsoDate(date);

    onChange?.({
      target: {
        name,
        value: isoValue,
      },
    });
    setViewDate(date);
    setOpen(false);
  };

  const handleBlur = (event) => {
    if (rootRef.current?.contains(event.relatedTarget)) return;

    onBlur?.({
      target: {
        name,
        value: value || "",
      },
    });
    setOpen(false);
  };

  const displayValue = formatDate(value);

  return (
    <div className="custom-date" ref={rootRef} onBlur={handleBlur}>
      <button
        type="button"
        className={`input custom-date-trigger ${className}`}
        style={style}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span className={!displayValue ? "custom-date-placeholder" : ""}>
          {displayValue || placeholder}
        </span>
        <span className="custom-date-icon" aria-hidden="true" />
      </button>

      {open ? (
        <div className="custom-date-popover" role="dialog" aria-label="Choose date">
          <div className="custom-date-header">
            <button type="button" onClick={() => handleChangeMonth(-1)} aria-label="Previous month">
              ‹
            </button>
            <strong>
              {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
            </strong>
            <button type="button" onClick={() => handleChangeMonth(1)} aria-label="Next month">
              ›
            </button>
          </div>

          <div className="custom-date-grid custom-date-days">
            {dayNames.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="custom-date-grid">
            {visibleDays.map((date) => {
              const isoValue = toIsoDate(date);
              const isMuted = date.getMonth() !== viewDate.getMonth();
              const isSelected = value === isoValue;
              const isToday = toIsoDate(new Date()) === isoValue;

              return (
                <button
                  type="button"
                  key={isoValue}
                  className={`${isMuted ? "muted" : ""} ${isSelected ? "selected" : ""} ${
                    isToday ? "today" : ""
                  }`}
                  onClick={() => handleSelectDate(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CustomDateInput;
